// cdn https://cdn.jsdelivr.net/npm/three@0.165.0/examples/jsm
// cdn https://cdn.jsdelivr.net/npm/three@0.165.0/build/three.module.min.js

import * as THREE from "./three.module.min.js";
import { GUI } from "./lil-gui.module.min.js";
import { OrbitControls } from "./OrbitControls.js";
import { GLTFLoader } from "./GLTFLoader.js";
import { EXRLoader } from "./EXRLoader.js";
import { RGBELoader } from "./RGBELoader.js";
import { RoomEnvironment } from "./RoomEnvironment.js";
// import { DRACOLoader } from "./DRACOLoader.js";

const scriptTag = document.getElementById("build3d-script");
const src = scriptTag.getAttribute("src").split("assets")[0];

let renderer = null;
let products = {
  first: {
    modelName: "ZUHE(11).glb",
    camera: null,
    scene: null,
    controls: null,
    variantExtension: null,
    variantParser: null,
    variants: null,
    meshes: {},
    GLTF: null,
    meshGroup: {
      PCB: {
        blocks: ["PC-left", "PC-right"],
        visible: true,
      },
      Case: {
        blocks: ["Case-left", "Case-right"],
        visible: true,
        defaultVariant: "Black",
      },
      Plate: {
        blocks: ["Plate-left", "Plate-lright"],
        visible: false,
      },
      Switches: {
        blocks: ["Switches-left", "Switches-right"],
        visible: false,
      },
      Keycaps: {
        blocks: ["Kecaps-left", "Kecaps-right"],
        visible: false,
      },
    },
  },
  second: {
    modelName: "Choc_Corne_V2.glb",
    camera: null,
    scene: null,
    controls: null,
    variantExtension: null,
    variantParser: null,
    variants: null,
    meshes: {},
    GLTF: null,
    meshGroup: {
      PCB: {
        blocks: ["PC-left", "PC-righ"],
        visible: true,
      },
      Bottom_case: {
        blocks: ["Bottom_case-left", "Bottom_case-right"],
        visible: true,
        defaultVariant: "Black",
      },
      Top_case: {
        blocks: ["Top_case-left", "Top_case-right"],
        visible: true,
        defaultVariant: "Black",
      },
      Switches: {
        blocks: ["Switches-left", "Switches-right"],
        visible: false,
      },
      Keycaps: {
        blocks: ["Kecaps-left", "Kecaps-right"],
        visible: false,
      },
    },
  },
};

window.build3d = {};
const config = {
  autoRotate: false,
  curProduct: "first",
};

init();

function init() {
  const container = document.getElementById("canvas3d");
  const width = container.clientWidth;
  const height = container.clientHeight;

  renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setClearColor(0xebecf0, 1); //设置背景颜色
  renderer.setPixelRatio(width / height);
  renderer.setSize(width, height);
  renderer.toneMapping = THREE.LinearToneMapping;
  renderer.toneMappingExposure = 0.6;
  container.appendChild(renderer.domElement);

  Object.keys(products).forEach((productName) => {
    let product = products[productName];
    product.camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 50);
    product.camera.position.set(-4, 0.6, 4);
    product.scene = new THREE.Scene();
    initLight(product);
    loadModel(product);
    initTexture2(product);
    initControls(product);
  });

  // initGUI();
  render();

  window.build3d.selectVariant = selectVariant;
  window.build3d.setMeshVisible = setMeshVisible;
  window.build3d.products = products;
  window.build3d.config = config;

  window.addEventListener("resize", () => onWindowResize(container.clientWidth, container.clientHeight));
}

function loadModel(product) {
  const loader = new GLTFLoader().setPath(`${src}assets/`);
  loader.load(product.modelName, function (gltf) {
    // console.log(gltf);
    product.GLTF = gltf;
    // gltf.scene.scale.set(10.0, 10.0, 10.0);
    gltf.scene.position.set(0, 0.6, 0);
    gltf.scene.children[0].position.set(0, 0, 0);
    gltf.scene.rotation.y = -Math.PI / 4;
    // gltf.scene.rotation.x = Math.PI / 4;
    for (let i = 0; i < gltf.scene.children.length; i++) {
      let child = gltf.scene.children[i];
      product.meshes[child.name] = child;
      child.visible = false;
    }
    product.scene.add(gltf.scene);
    // product.scene.rotation.y = Math.PI / 2;
    // Details of the KHR_materials_variants extension used here can be found below
    // https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_materials_variants
    product.variantParser = gltf.parser;
    product.variantExtension = gltf.userData.gltfExtensions["KHR_materials_variants"];
    product.variants = product.variantExtension.variants.map((variant) => variant.name);

    for (let meshName in product.meshGroup) {
      let mesh = product.meshGroup[meshName];
      if (mesh.visible) {
        setMeshVisible(product, meshName, true);
        if (mesh.defaultVariant) {
          selectVariant(product, mesh.defaultVariant, mesh.blocks);
        }
      }
    }
    dispatchCustomEvent("build3d:loaded");
  });
}

function setMeshVisible(product, blockName, visible) {
  // console.log("setmesh visible", blockName, visible, product.meshGroup[blockName].blocks);
  product.meshGroup[blockName].blocks.forEach((block) => {
    product.meshes[block].visible = visible;
  });
}

//https://cdn.jsdelivr.net/npm/three@0.165.0/examples/jsm/libs/draco

function selectVariant(product, variantName, meshNames) {
  // console.log("selectVariant", variantName, meshNames);
  const variantIndex = product.variantExtension.variants.findIndex((v) => v.name.includes(variantName));

  product.GLTF.scene.children.forEach((child) => {
    if (meshNames.includes(child.name)) {
      child.traverse(async (object) => {
        if (!object.isMesh || !object.userData.gltfExtensions) return;

        const meshVariantDef = object.userData.gltfExtensions["KHR_materials_variants"];

        if (!meshVariantDef) return;

        if (!object.userData.originalMaterial) {
          object.userData.originalMaterial = object.material;
        }

        const mapping = meshVariantDef.mappings.find((mapping) => mapping.variants.includes(variantIndex));

        if (mapping) {
          object.material = await product.variantParser.getDependency("material", mapping.material);
          product.variantParser.assignFinalMaterial(object);
        } else {
          object.material = object.userData.originalMaterial;
        }
      });
    }
  });
}

function onWindowResize(width, height) {
  // console.log("resize", width, height);
  let { camera } = products[config.curProduct];
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  renderer.setSize(width, height);
  render();
}

function render() {
  let { controls, scene, camera } = products[config.curProduct];
  controls.autoRotate = config.autoRotate;
  controls.update();
  renderer.render(scene, camera);
  window.requestAnimationFrame(render);
}

function initTexture2(product) {
  const pmremGenerator = new THREE.PMREMGenerator(renderer); // 使用hdr作为背景色
  pmremGenerator.compileEquirectangularShader();
  let envMap = pmremGenerator.fromScene(new RoomEnvironment()).texture;
  product.scene.environment = envMap; // 给场景添加环境光效果
  // product.scene.background = envMap; // 给场景添加背景图
}

function initLight(product) {
  // const hemiLight = new THREE.HemisphereLight();
  // const ambientLight = new THREE.AmbientLight(0xffffff, 1);
  const light1 = new THREE.DirectionalLight(0xffffff, 1);
  const light2 = new THREE.DirectionalLight(0xffffff, 1);
  light1.position.set(0, 0, 4); // ~60º
  light2.position.set(-4, 0, 3); // ~60º
  // product.scene.add(hemiLight, ambientLight);
  product.scene.add(light1, light2);
}

function initGUI() {
  let gui = new GUI();
  // gui.add(renderer, "toneMappingExposure", 0, 4, 0.01);
  gui.add(config, "curProduct", ["first", "second"]);
}

function initControls(product) {
  let controls = new OrbitControls(product.camera, renderer.domElement);
  controls.minDistance = 2;
  controls.maxDistance = 10;
  controls.autoRotateSpeed = 2.0;
  controls.target.set(0, 0.5, -0.2);
  product.controls = controls;
}
